import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { getWorkspace, setWorkspace } from "../lib/workspaceStore";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";

function SortableRow({ id, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between rounded-lg border p-2"
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">#{index + 1}</span>
        <button
          type="button"
          className="cursor-grab rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/60"
          {...attributes}
          {...listeners}
          aria-label="Drag"
        >
          drag
        </button>
        <span className="font-medium">{id}</span>
      </div>

      <Button variant="outline" size="sm" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

const DEPTS = ["All", "Creative", "Strategy", "Social", "ClientSuccess", "Brand", "Sales"];

export default function PackBuilderPage() {
  const qc = useQueryClient();
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);

  const [people, setPeople] = useState([]);
  const [packs, setPacks] = useState([]);
  const [err, setErr] = useState(null);

  // filters
  const [dept, setDept] = useState("All");
  const [presentersOnly, setPresentersOnly] = useState(false);
  const [q, setQ] = useState("");

  // builder state
  const [sequence, setSequence] = useState([]);
  const [packName, setPackName] = useState("");
  const [packDesc, setPackDesc] = useState("");
  const [platform, setPlatform] = useState("meta");

  // spokesperson options for running packs
  const [spokespersonMode, setSpokespersonMode] = useState(false);
  const [presenterKeys, setPresenterKeys] = useState(["cole_creative"]);
  const [variantsPerPresenter, setVariantsPerPresenter] = useState(2);
  const [presenterStyle, setPresenterStyle] = useState("");

  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const [estimate, setEstimate] = useState(null);

  // editing state
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPlatform, setEditPlatform] = useState("meta");
  const [editSequence, setEditSequence] = useState([]);
  const [editSpokesperson, setEditSpokesperson] = useState(false);
  const [editPresenterKeys, setEditPresenterKeys] = useState(["cole_creative"]);
  const [editVariantsPer, setEditVariantsPer] = useState(2);
  const [editPresenterStyle, setEditPresenterStyle] = useState("");

  // sharing state
  const [shareLoading, setShareLoading] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    loadWorkspaces();
  }, []);

  useEffect(() => {
    if (workspace) {
      load(workspace.id);
    }
  }, [workspace]);

  // Calculate cost estimate when sequence changes
  useEffect(() => {
    if (!workspace) return;

    async function calc() {
      try {
        const res = await api.post("/api/credits/estimate", {
          sequence,
          spokesperson_mode: spokespersonMode,
          presenter_keys: spokespersonMode ? presenterKeys : null,
          variants_per_presenter: spokespersonMode ? variantsPerPresenter : 1,
        });
        setEstimate(res.data);
      } catch {
        setEstimate(null);
      }
    }

    if (sequence.length > 0) calc();
    else setEstimate(null);
  }, [workspace, sequence, spokespersonMode, presenterKeys, variantsPerPresenter]);

  async function loadWorkspaces() {
    try {
      const [ppl, ws] = await Promise.all([
        api.get("/api/ai/people"),
        api.get("/api/workspaces"),
      ]);
      setPeople(ppl.data.items || []);
      setWorkspaces(ws.data.items || []);

      // Restore or pick first workspace
      const stored = getWorkspace();
      const wsList = ws.data.items || [];
      if (stored && wsList.find((w) => w.id === stored.id)) {
        setWorkspace(stored);
      } else if (wsList.length > 0) {
        setWorkspace(wsList[0]);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load.");
    }
  }

  async function load(workspaceId) {
    setErr(null);
    try {
      const pks = await api.get(`/api/workspaces/${workspaceId}/packs`);
      setPacks(pks.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load packs.");
    }
  }

  const filteredPeople = useMemo(() => {
    return people
      .filter((p) => (dept === "All" ? true : p.department === dept))
      .filter((p) => (presentersOnly ? !!p.can_present : true))
      .filter((p) => {
        const t = `${p.name} ${p.title} ${p.department} ${p.purpose}`.toLowerCase();
        return t.includes(q.toLowerCase());
      });
  }, [people, dept, presentersOnly, q]);

  const presenters = useMemo(() => people.filter((p) => p.can_present), [people]);

  function addToSequence(key) {
    setSequence((prev) => {
      if (prev.length >= 12) return prev;
      return [...prev, key];
    });
  }

  function removeAt(i) {
    setSequence((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function savePack() {
    if (!workspace) return;
    if (packName.trim().length < 2) {
      setErr("Pack name is required.");
      return;
    }
    if (sequence.length === 0) {
      setErr("Add at least 1 AI person to the pack.");
      return;
    }

    setErr(null);
    setSaving(true);
    try {
      await api.post(`/api/workspaces/${workspace.id}/packs`, {
        name: packName,
        description: packDesc || null,
        platform,
        sequence,
        settings: {
          spokesperson_mode: spokespersonMode,
          presenter_keys: spokespersonMode ? presenterKeys : [],
          variants_per_presenter: spokespersonMode ? variantsPerPresenter : 1,
          presenter_style: spokespersonMode ? (presenterStyle || null) : null,
        },
      });
      setPackName("");
      setPackDesc("");
      setSequence([]);
      setSpokespersonMode(false);
      setPresenterKeys(["cole_creative"]);
      setVariantsPerPresenter(2);
      setPresenterStyle("");
      qc.invalidateQueries({ queryKey: ["packs", workspace.id] });
      await load(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save pack.");
    } finally {
      setSaving(false);
    }
  }

  async function deletePack(packId) {
    if (!workspace) return;
    setErr(null);
    try {
      await api.delete(`/api/workspaces/${workspace.id}/packs/${packId}`);
      qc.invalidateQueries({ queryKey: ["packs", workspace.id] });
      await load(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to delete pack.");
    }
  }

  async function duplicatePack(packId) {
    if (!workspace) return;
    setErr(null);
    try {
      await api.post(`/api/workspaces/${workspace.id}/packs/${packId}/duplicate`);
      qc.invalidateQueries({ queryKey: ["packs", workspace.id] });
      await load(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to duplicate pack.");
    }
  }

  function openEdit(pack) {
    setEditing(pack);
    setEditName(pack.name || "");
    setEditDesc(pack.description || "");
    setEditPlatform(pack.platform || "meta");
    setEditSequence(Array.isArray(pack.sequence) ? pack.sequence : []);

    const s = pack.settings || {};
    setEditSpokesperson(!!s.spokesperson_mode);
    setEditPresenterKeys(Array.isArray(s.presenter_keys) && s.presenter_keys.length ? s.presenter_keys : ["cole_creative"]);
    setEditVariantsPer(Number(s.variants_per_presenter || 1));
    setEditPresenterStyle(s.presenter_style || "");
  }

  function closeEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!workspace || !editing) return;

    if (editName.trim().length < 2) {
      setErr("Pack name is required.");
      return;
    }
    if (!editSequence.length) {
      setErr("Pack sequence cannot be empty.");
      return;
    }

    setErr(null);
    try {
      await api.patch(`/api/workspaces/${workspace.id}/packs/${editing.id}`, {
        name: editName,
        description: editDesc || null,
        platform: editPlatform,
        sequence: editSequence,
        settings: {
          spokesperson_mode: editSpokesperson,
          presenter_keys: editSpokesperson ? editPresenterKeys : [],
          variants_per_presenter: editSpokesperson ? editVariantsPer : 1,
          presenter_style: editSpokesperson ? (editPresenterStyle || null) : null,
        },
      });

      setEditing(null);
      qc.invalidateQueries({ queryKey: ["packs", workspace.id] });
      await load(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to update pack.");
    }
  }

  async function runPack(packId) {
    if (!workspace) return;
    setErr(null);
    setRunningId(packId);

    try {
      const res = await api.post(`/api/workspaces/${workspace.id}/packs/${packId}/run`, {
        product_or_service: "Use AI Studio to enter your product/service details",
        goal: "sales",
        notes: "Run using the saved pack template.",
      });
      qc.invalidateQueries({ queryKey: ["library", workspace.id] });
      const genId = res.data.id;
      window.location.href = `/app/library/${genId}`;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to run pack.");
    } finally {
      setRunningId(null);
    }
  }

  async function sharePack(packId, targetWorkspaceId) {
    if (!workspace) return;
    setShareLoading(packId);
    setErr(null);

    try {
      await api.post(`/api/workspaces/${workspace.id}/packs/${packId}/share`, {
        target_workspace_id: targetWorkspaceId,
      });
      qc.invalidateQueries({ queryKey: ["packs", targetWorkspaceId] });
      alert("Pack shared!");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to share pack.");
    } finally {
      setShareLoading(null);
    }
  }

  if (!workspace) {
    return (
      <div className="space-y-4" data-testid="pack-builder-page">
        <div className="text-2xl font-semibold">Pack Builder</div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="text-sm text-muted-foreground">{err || "No workspace selected."}</div>
            <Link to="/app/workspaces">
              <Button className="mt-3">Go to Workspaces</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pack-builder-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">Pack Builder</div>
          <div className="text-sm text-muted-foreground">
            Build custom AI teams and save them as reusable templates.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={workspace.id}
            onChange={(e) => {
              const ws = workspaces.find((w) => w.id === e.target.value);
              if (ws) setWorkspace(ws);
            }}
            className="rounded-md border px-2 py-1 text-sm"
            data-testid="workspace-select"
          >
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </select>
          <Link to="/app/studio" className="text-sm text-muted-foreground hover:underline">
            back to studio
          </Link>
        </div>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Roster */}
        <Card>
          <CardHeader>
            <CardTitle>AI Roster</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input 
              placeholder="Search staff…" 
              value={q} 
              onChange={(e) => setQ(e.target.value)} 
              data-testid="search-input"
            />

            <div className="flex flex-wrap gap-2">
              {DEPTS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDept(d)}
                  className={[
                    "rounded-lg border px-3 py-2 text-sm transition",
                    dept === d ? "bg-muted font-medium" : "hover:bg-muted/60",
                  ].join(" ")}
                  type="button"
                  data-testid={`dept-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setPresentersOnly((v) => !v)}
              className={[
                "w-full rounded-lg border px-3 py-2 text-sm transition",
                presentersOnly ? "bg-muted font-medium" : "hover:bg-muted/60",
              ].join(" ")}
              data-testid="presenters-only-toggle"
            >
              {presentersOnly ? "Showing presenters only" : "Show presenters only"}
            </button>

            <Separator />

            <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
              {filteredPeople.map((p) => (
                <div key={p.key} className="rounded-xl border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{p.name}</div>
                    <Badge variant="outline">{p.department}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">{p.title}</div>
                  <div className="mt-2 text-xs text-muted-foreground">{p.purpose}</div>
                  <Button 
                    className="mt-3 w-full" 
                    onClick={() => addToSequence(p.key)}
                    data-testid={`add-${p.key}`}
                  >
                    Add to pack
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Builder */}
        <Card>
          <CardHeader>
            <CardTitle>Build a pack</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="text-sm font-medium">Pack name</label>
            <Input 
              value={packName} 
              onChange={(e) => setPackName(e.target.value)} 
              placeholder="e.g., Meta Sales Team" 
              data-testid="pack-name-input"
            />

            <label className="text-sm font-medium">Description</label>
            <Input 
              value={packDesc} 
              onChange={(e) => setPackDesc(e.target.value)} 
              placeholder="Optional" 
              data-testid="pack-desc-input"
            />

            <label className="text-sm font-medium">Default platform</label>
            <select
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              data-testid="platform-select"
            >
              {["meta","tiktok","youtube","linkedin","x","pinterest","website"].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <Separator />

            <div className="text-sm font-medium">Sequence (order matters)</div>
            {sequence.length === 0 ? (
              <div className="text-sm text-muted-foreground">Add AI people from the roster.</div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over) return;
                  if (active.id === over.id) return;

                  setSequence((items) => {
                    const oldIndex = items.indexOf(String(active.id));
                    const newIndex = items.indexOf(String(over.id));
                    return arrayMove(items, oldIndex, newIndex);
                  });
                }}
              >
                <SortableContext items={sequence} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {sequence.map((k, i) => (
                      <SortableRow key={`${k}-${i}`} id={k} index={i} onRemove={() => removeAt(i)} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            <Separator />

            {/* Spokesperson Settings (saved with pack) */}
            <div className="rounded-xl border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Spokesperson Settings</div>
                  <div className="text-xs text-muted-foreground">Save defaults with this pack</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSpokespersonMode((v) => !v)}
                  className={[
                    "rounded-lg border px-3 py-2 text-xs transition",
                    spokespersonMode ? "bg-muted font-medium" : "hover:bg-muted/60",
                  ].join(" ")}
                  data-testid="build-spokesperson-toggle"
                >
                  {spokespersonMode ? "On" : "Off"}
                </button>
              </div>

              {spokespersonMode && (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-medium">Presenters</label>
                    <div className="flex flex-wrap gap-1">
                      {presenters.map((p) => {
                        const active = presenterKeys.includes(p.key);
                        return (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => {
                              setPresenterKeys((prev) => {
                                if (active) return prev.filter((x) => x !== p.key);
                                if (prev.length >= 5) return prev;
                                return [...prev, p.key];
                              });
                            }}
                            className={[
                              "rounded-lg border px-2 py-1 text-xs transition",
                              active ? "bg-muted font-medium" : "hover:bg-muted/60",
                            ].join(" ")}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-2 items-center">
                    <label className="text-xs font-medium">Variants:</label>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setVariantsPerPresenter(n)}
                        className={[
                          "rounded-lg border px-2 py-1 text-xs transition",
                          variantsPerPresenter === n ? "bg-muted font-medium" : "hover:bg-muted/60",
                        ].join(" ")}
                      >
                        {n}
                      </button>
                    ))}
                  </div>

                  <Input
                    value={presenterStyle}
                    onChange={(e) => setPresenterStyle(e.target.value)}
                    placeholder="Style: premium, confident..."
                    className="text-xs h-8"
                  />
                </>
              )}
            </div>

            {estimate && sequence.length > 0 && (
              <div className="rounded-xl border p-3 space-y-1" data-testid="pack-cost-estimate">
                <div className="text-sm font-medium">Credit estimate</div>
                <div className="text-xs text-muted-foreground">
                  Per call: {estimate.per_call_cost} • Base calls: {estimate.base_calls}
                  {estimate.variant_calls > 0 ? ` • Variant calls: ${estimate.variant_calls}` : ""}
                </div>
                <div className="text-sm">
                  Total calls: <span className="font-medium">{estimate.total_calls}</span> •
                  Estimated cost: <span className="font-medium">{estimate.total_cost}</span> credits
                </div>
              </div>
            )}

            <Button 
              onClick={savePack} 
              disabled={saving || sequence.length === 0 || packName.trim().length < 2}
              data-testid="save-pack-btn"
            >
              {saving ? "Saving…" : "Save Pack Template"}
            </Button>

            <div className="text-xs text-muted-foreground">
              Tip: Start with 3–5 AI people. Too many can waste credits.
            </div>
          </CardContent>
        </Card>

        {/* Saved templates */}
        <Card>
          <CardHeader>
            <CardTitle>Saved templates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {packs.length === 0 ? (
              <div className="text-sm text-muted-foreground">No templates yet.</div>
            ) : (
              <div className="space-y-2">
                {packs.map((p) => (
                  <div key={p.id} className="rounded-xl border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium">{p.name}</div>
                      <Badge variant="secondary">{p.platform}</Badge>
                    </div>
                    {p.description && (
                      <div className="mt-1 text-sm text-muted-foreground">{p.description}</div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      {p.sequence.length} AI • {new Date(p.created_at).toLocaleString()}
                      {p.settings?.spokesperson_mode && (
                        <span className="ml-2 text-primary">• Spokesperson</span>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-5 gap-2">
                      <Button onClick={() => runPack(p.id)} disabled={runningId === p.id}>
                        {runningId === p.id ? "Running…" : "Run"}
                      </Button>
                      <Button variant="outline" onClick={() => openEdit(p)}>
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const target = prompt("Enter target workspace ID");
                          if (target) sharePack(p.id, target);
                        }}
                        disabled={shareLoading === p.id}
                      >
                        {shareLoading === p.id ? "Sharing..." : "Share"}
                      </Button>
                      <Button variant="outline" onClick={() => duplicatePack(p.id)}>
                        Duplicate
                      </Button>
                      <Button variant="outline" onClick={() => deletePack(p.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Separator />
            <div className="space-y-2">
              <Link to="/app/marketplace">
                <Button variant="default" className="w-full">Browse Marketplace</Button>
              </Link>
              <Link to="/app/library">
                <Button variant="outline" className="w-full">Open Library</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Pack Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b p-4">
              <div>
                <div className="text-lg font-semibold">Edit Template</div>
                <div className="text-sm text-muted-foreground">{editing.name}</div>
              </div>
              <Button variant="outline" onClick={() => setEditing(null)}>
                Close
              </Button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Platform</label>
                  <select
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={editPlatform}
                    onChange={(e) => setEditPlatform(e.target.value)}
                  >
                    {["meta", "tiktok", "youtube", "linkedin", "x", "pinterest", "website"].map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2">
                {/* Sequence editor */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">Sequence</div>
                  <div className="text-xs text-muted-foreground">
                    Remove and re-add in the order you want. (Drag-and-drop can be added here next.)
                  </div>

                  <div className="space-y-2">
                    {editSequence.map((k, i) => (
                      <div key={`${k}-${i}`} className="flex items-center justify-between rounded-lg border p-2">
                        <div className="text-sm">
                          <span className="text-muted-foreground mr-2">#{i + 1}</span>
                          <span className="font-medium">{k}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (i === 0) return;
                              setEditSequence((prev) => {
                                const copy = [...prev];
                                const t = copy[i - 1];
                                copy[i - 1] = copy[i];
                                copy[i] = t;
                                return copy;
                              });
                            }}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (i === editSequence.length - 1) return;
                              setEditSequence((prev) => {
                                const copy = [...prev];
                                const t = copy[i + 1];
                                copy[i + 1] = copy[i];
                                copy[i] = t;
                                return copy;
                              });
                            }}
                          >
                            ↓
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEditSequence((prev) => prev.filter((_, idx) => idx !== i))}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border p-3">
                    <div className="text-sm font-medium mb-2">Add staff</div>
                    <div className="flex flex-wrap gap-2">
                      {people.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setEditSequence((prev) => (prev.length >= 12 ? prev : [...prev, p.key]))}
                          className="rounded-lg border px-3 py-2 text-sm hover:bg-muted/60"
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Max 12 AI people per template (MVP).
                    </div>
                  </div>
                </div>

                {/* Settings editor */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">Template Settings</div>

                  <div className="rounded-xl border p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Spokesperson Mode</div>
                        <div className="text-sm text-muted-foreground">
                          Save presenter rotation settings in this template.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditSpokesperson((v) => !v)}
                        className={[
                          "rounded-lg border px-3 py-2 text-sm transition",
                          editSpokesperson ? "bg-muted font-medium" : "hover:bg-muted/60",
                        ].join(" ")}
                      >
                        {editSpokesperson ? "On" : "Off"}
                      </button>
                    </div>

                    {editSpokesperson && (
                      <>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Presenters (up to 5)</label>
                          <div className="flex flex-wrap gap-2">
                            {people
                              .filter((p) => p.can_present)
                              .map((p) => {
                                const active = editPresenterKeys.includes(p.key);
                                return (
                                  <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => {
                                      setEditPresenterKeys((prev) => {
                                        if (active) return prev.filter((x) => x !== p.key);
                                        if (prev.length >= 5) return prev;
                                        return [...prev, p.key];
                                      });
                                    }}
                                    className={[
                                      "rounded-lg border px-3 py-2 text-sm transition",
                                      active ? "bg-muted font-medium" : "hover:bg-muted/60",
                                    ].join(" ")}
                                  >
                                    {p.name}
                                  </button>
                                );
                              })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Selected: {editPresenterKeys.length}
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Variants per presenter</label>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                  key={n}
                                  type="button"
                                  onClick={() => setEditVariantsPer(n)}
                                  className={[
                                    "flex-1 rounded-lg border px-3 py-2 text-sm transition",
                                    editVariantsPer === n ? "bg-muted font-medium" : "hover:bg-muted/60",
                                  ].join(" ")}
                                >
                                  {n}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-medium">Presenter style (optional)</label>
                            <Input
                              value={editPresenterStyle}
                              onChange={(e) => setEditPresenterStyle(e.target.value)}
                              placeholder="premium, energetic, calm…"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveEdit}>Save changes</Button>
                    <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
